import React, { useRef, useState } from 'react'
import Modal from '../../../../components/Modal'
import CloseIcon from '../../../../assets/icon/CloseIcon'
import CheckIcon from '../../../../assets/icon/CheckIcon'
import InfoIcon from '../../../../assets/icon/InfoIcon'
import styles from './UploadDocumentsModal.module.scss'
import DocumentService from '../../../../services/document.service'

interface UploadDocumentsModalProps {
    isOpen: boolean
    projectId: string
    onClose: () => void
    onUploaded: () => Promise<void>
    onSuccess: (uploadedIds?: Array<string | number>) => void
    onError: (message: string) => void
}

type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'error'

interface UploadItem {
    id: string
    file: File
    status: UploadItemStatus
}

const SUPPORTED_DOCUMENT_EXTENSIONS = ['csv', 'jpeg', 'jpg', 'json', 'md', 'pdf', 'png', 'txt']
const SUPPORTED_DOCUMENT_EXTENSIONS_LABEL = SUPPORTED_DOCUMENT_EXTENSIONS.join(', ')
const SUPPORTED_DOCUMENT_ACCEPT = SUPPORTED_DOCUMENT_EXTENSIONS.map((extension) => `.${extension}`).join(',')

function makeItemId(file: File, index: number) {
    return `${file.name}-${file.size}-${file.lastModified}-${index}-${Date.now()}`
}

function getFileExtension(fileName: string) {
    const extension = fileName.split('.').pop()
    return extension ? extension.toLowerCase() : ''
}

function isSupportedDocumentFile(file: File) {
    return SUPPORTED_DOCUMENT_EXTENSIONS.includes(getFileExtension(file.name))
}

export default function UploadDocumentsModal({
    isOpen,
    projectId,
    onClose,
    onUploaded,
    onSuccess,
    onError,
}: UploadDocumentsModalProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const [items, setItems] = useState<UploadItem[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)

    const openFilePicker = () => {
        inputRef.current?.click()
    }

    const appendFiles = (files: File[]) => {
        if (!files.length) {
            return
        }

        const supportedFiles = files.filter(isSupportedDocumentFile)
        const hasUnsupportedFiles = supportedFiles.length !== files.length

        if (hasUnsupportedFiles) {
            onError(`Формат файла не поддерживается. Разрешенные форматы: ${SUPPORTED_DOCUMENT_EXTENSIONS_LABEL}.`)
        }

        if (!supportedFiles.length) {
            return
        }

        setItems((prev) => [
            ...prev,
            ...supportedFiles.map((file, index) => ({
                id: makeItemId(file, index),
                file,
                status: 'pending' as UploadItemStatus,
            })),
        ])
    }

    const handleClose = () => {
        if (isSubmitting) return

        setItems([])
        onClose()
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        appendFiles(Array.from(event.target.files || []))
        event.target.value = ''
    }

    const handleRemove = (id: string) => {
        if (isSubmitting) return

        setItems((prev) => prev.filter((item) => item.id !== id))
    }

    const handleImport = async () => {
        if (!items.length || isSubmitting) return

        setIsSubmitting(true)
        let hasError = false
        const uploadedIds: Array<string | number> = []

        await Promise.all(items.map(async (item) => {
            setItems((prev) => prev.map((current) => (
                current.id === item.id
                    ? { ...current, status: 'uploading' }
                    : current
            )))

            try {
                const uploaded = await DocumentService.upload(projectId, item.file, item.file.name)
                if (uploaded?.id) {
                    uploadedIds.push(uploaded.id)
                }
                setItems((prev) => prev.map((current) => (
                    current.id === item.id
                        ? { ...current, status: 'done' }
                        : current
                )))
            } catch (error) {
                hasError = true
                console.error('Failed to upload document:', error)
                setItems((prev) => prev.map((current) => (
                    current.id === item.id
                        ? { ...current, status: 'error' }
                        : current
                )))
            }
        }))

        await onUploaded()

        if (!hasError) {
            setItems([])
            setIsSubmitting(false)
            onClose()
            onSuccess(uploadedIds)
            return
        }

        onError(`Не удалось загрузить документ. Проверьте формат файла. Разрешенные форматы: ${SUPPORTED_DOCUMENT_EXTENSIONS_LABEL}.`)
        setIsSubmitting(false)
    }

    return (
        <>
            <Modal isOpen={isOpen} onRequestClose={handleClose} width={446}>
                <div className={styles.uploadModal}>
                    <div className={styles.uploadModal__close} onClick={handleClose}>
                        <CloseIcon />
                    </div>

                    <div className={styles.uploadModal__content}>
                        <div className={styles.uploadModal__title}>
                            Выберите файл для загрузки
                        </div>

                        <div className={styles.uploadModal__field}>
                            <div
                                className={styles.uploadModal__dropzone}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                    event.preventDefault()
                                    appendFiles(Array.from(event.dataTransfer.files || []))
                                }}
                            >
                                <div
                                    className={styles.uploadModal__dropIcon}
                                    onClick={openFilePicker}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 16V6M12 6L8 10M12 6L16 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M6 15.5V17C6 18.1046 6.89543 19 8 19H16C17.1046 19 18 18.1046 18 17V15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className={styles.uploadModal__dropText}>
                                    Перетащите файл сюда <button type="button" onClick={openFilePicker}>или прикрепите</button>
                                </div>
                                <div className={styles.uploadModal__dropMeta}>
                                    <div className={`${styles.uploadModal__dropSubtext} ${styles.uploadModal__dropSubtextSingleLine}`}>
                                        Поддерживаемые типы файлов: {SUPPORTED_DOCUMENT_EXTENSIONS_LABEL}
                                    </div>
                                    <div className={styles.uploadModal__dropSubtext}>
                                        Максимальный вес: 20 MB
                                    </div>
                                </div>
                            </div>
                        </div>

                        {items.length > 0 && (
                            <div className={styles.uploadModal__field}>
                                <div className={styles.uploadModal__list}>
                                    {items.map((item) => (
                                        <div key={item.id} className={styles.uploadModal__item}>
                                            <div className={styles.uploadModal__itemStatus}>
                                                {item.status === 'uploading' && <div className={styles.uploadModal__spinner} />}
                                                {item.status === 'done' && (
                                                    <div className={styles.uploadModal__check}>
                                                        <CheckIcon />
                                                    </div>
                                                )}
                                                {item.status === 'pending' && (
                                                    <div className={styles.uploadModal__pending}>
                                                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                )}
                                                {item.status === 'error' && <div className={styles.uploadModal__error} />}
                                            </div>
                                            <div className={styles.uploadModal__itemName}>{item.file.name}</div>
                                            <button
                                                type="button"
                                                className={styles.uploadModal__itemRemove}
                                                onClick={() => handleRemove(item.id)}
                                                disabled={isSubmitting}
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={styles.uploadModal__field}>
                            <div className={styles.uploadModal__info}>
                                <InfoIcon />
                                <span>Импортированные документы будут видны всем участникам данного проекта</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`${styles.uploadModal__submit} ${(!items.length || isSubmitting) ? styles.uploadModal__submitDisabled : ''}`}
                            onClick={handleImport}
                            disabled={!items.length || isSubmitting}
                        >
                            {isSubmitting ? 'Импортирование...' : 'Импортировать в проект'}
                        </button>
                    </div>

                    <input
                        ref={inputRef}
                        type="file"
                        hidden
                        multiple
                        accept={SUPPORTED_DOCUMENT_ACCEPT}
                        onChange={handleInputChange}
                    />
                </div>
            </Modal>
        </>
    )
}
